import { z } from 'zod';
import { ESTADOS, ESTADOS_CIVIS, ETAPAS, ORIGENS_CAMPO, PORTES, REGIMES_BENS, REGIMES_TRIBUTARIOS, SITUACOES, STATUS_DOCUMENTO, TIPOS_DOCUMENTO, TIPOS_EMPRESA, } from './dominio.js';
import { validarCapital, validarCpf, validarQuotas, validarTelefoneBr } from './validadores.js';
// ---------------------------------------------------------------------------
// Enums zod
// ---------------------------------------------------------------------------
export const estadoSchema = z.enum(ESTADOS);
export const tipoEmpresaSchema = z.enum(TIPOS_EMPRESA);
export const etapaSchema = z.enum(ETAPAS);
export const situacaoSchema = z.enum(SITUACOES);
export const estadoCivilSchema = z.enum(ESTADOS_CIVIS);
export const regimeBensSchema = z.enum(REGIMES_BENS);
export const tipoDocumentoSchema = z.enum(TIPOS_DOCUMENTO);
export const statusDocumentoSchema = z.enum(STATUS_DOCUMENTO);
export const origemCampoSchema = z.enum(ORIGENS_CAMPO);
export const regimeTributarioSchema = z.enum(REGIMES_TRIBUTARIOS);
export const porteSchema = z.enum(PORTES);
// ---------------------------------------------------------------------------
// Entrada: webhook do formulário do site (Fluent Forms)
// ---------------------------------------------------------------------------
export const pedidoWebhookSchema = z.object({
    /** Identificador da submissão no Fluent Forms, para idempotência. */
    referencia_externa: z.string().min(1).max(120),
    estado: estadoSchema,
    tipo_empresa: tipoEmpresaSchema,
    nome_contato: z.string().min(2).max(200),
    telefone_contato: z.string().refine(validarTelefoneBr, 'Telefone inválido'),
    email_contato: z.email().optional().or(z.literal('')),
    nome_empresa_pretendido: z.string().max(200).optional().or(z.literal('')),
    municipio: z.string().max(120).optional().or(z.literal('')),
    plano: z.string().max(120).optional().or(z.literal('')),
    observacoes: z.string().max(2000).optional().or(z.literal('')),
});
// ---------------------------------------------------------------------------
// Questionário do cliente (etapa 2), em linguagem simples
// ---------------------------------------------------------------------------
export const socioQuestionarioSchema = z.object({
    id: z.uuid().optional(),
    nome: z.string().min(3, 'Informe o nome completo').max(200),
    cpf: z.string().refine(validarCpf, 'CPF inválido'),
    telefone: z.string().refine(validarTelefoneBr, 'Telefone inválido'),
    email: z.email('E-mail inválido').optional().or(z.literal('')),
    estado_civil: estadoCivilSchema,
    regime_bens: regimeBensSchema.optional().nullable(),
    administrador: z.boolean(),
    percentual_quotas: z.number().min(0).max(100),
    /** Para SP a assinatura exige gov.br Ouro; verificar cedo evita atraso na etapa 8. */
    nivel_govbr: z.enum(['bronze', 'prata', 'ouro', 'nao_sei']).default('nao_sei'),
});
export const enderecoSchema = z.object({
    cep: z.string().min(8).max(9),
    logradouro: z.string().min(1).max(200),
    numero: z.string().min(1).max(20),
    complemento: z.string().max(100).optional().or(z.literal('')),
    bairro: z.string().min(1).max(120),
    municipio: z.string().min(1).max(120),
    uf: z.string().length(2),
});
export const questionarioSchema = z
    .object({
    nome_empresarial_opcoes: z
        .array(z.string().min(2).max(200))
        .min(1, 'Informe pelo menos um nome')
        .max(3),
    atividades_descricao: z
        .string()
        .min(10, 'Descreva com suas palavras o que a empresa vai fazer')
        .max(2000),
    capital_social_centavos: z.number().int().positive(),
    endereco_empresa: enderecoSchema,
    endereco_e_residencial: z.boolean().default(false),
    inscricao_imobiliaria: z.string().max(60).optional().or(z.literal('')),
    faturamento_previsto_anual_centavos: z.number().int().nonnegative().optional().nullable(),
    tera_funcionarios: z.boolean().default(false),
    socios: z.array(socioQuestionarioSchema).min(1, 'Informe pelo menos um sócio').max(10),
})
    .superRefine((q, ctx) => {
    const erroCapital = validarCapital(q.capital_social_centavos);
    if (erroCapital)
        ctx.addIssue({ code: 'custom', path: ['capital_social_centavos'], message: erroCapital });
    const erroQuotas = validarQuotas(q.socios.map((s) => s.percentual_quotas));
    if (erroQuotas)
        ctx.addIssue({ code: 'custom', path: ['socios'], message: erroQuotas });
    if (!q.socios.some((s) => s.administrador))
        ctx.addIssue({
            code: 'custom',
            path: ['socios'],
            message: 'Pelo menos um sócio precisa ser administrador.',
        });
    const cpfs = new Set();
    for (const [i, s] of q.socios.entries()) {
        const c = s.cpf.replace(/\D/g, '');
        if (cpfs.has(c))
            ctx.addIssue({ code: 'custom', path: ['socios', i, 'cpf'], message: 'CPF repetido.' });
        cpfs.add(c);
        if (s.estado_civil === 'casado' && !s.regime_bens)
            ctx.addIssue({
                code: 'custom',
                path: ['socios', i, 'regime_bens'],
                message: 'Informe o regime de bens.',
            });
    }
});
/** EI só tem um titular. Aplicado pela API conforme o tipo da abertura. */
export function validarQuestionarioParaTipo(q, tipo) {
    if (tipo === 'EI' && q.socios.length !== 1)
        return 'Empresário individual tem um único titular.';
    if (tipo === 'LTDA' && q.socios.length < 1)
        return 'Sociedade limitada precisa de pelo menos um sócio.';
    return null;
}
// ---------------------------------------------------------------------------
// Saídas estruturadas da IA
// ---------------------------------------------------------------------------
export const legibilidadeSchema = z.object({
    legivel: z.boolean(),
    tipo_detectado: tipoDocumentoSchema.or(z.literal('desconhecido')),
    motivo: z.string().max(300),
    /** Sugestão para o cliente quando ilegível, em linguagem simples. */
    orientacao: z.string().max(300).optional(),
});
const campoExtraidoSchema = (valor) => z.object({
    valor: valor.nullable(),
    confianca: z.number().min(0).max(1),
    /** Trecho literal do documento de onde o valor foi lido. */
    trecho: z.string().max(300).nullable(),
});
export const extracaoIdentidadeSchema = z.object({
    tipo: z.literal('identidade'),
    documento: z.enum(['RG', 'CNH', 'RNE', 'passaporte', 'outro']),
    nome: campoExtraidoSchema(z.string()),
    cpf: campoExtraidoSchema(z.string()),
    rg_numero: campoExtraidoSchema(z.string()),
    rg_orgao_emissor: campoExtraidoSchema(z.string()),
    rg_uf: campoExtraidoSchema(z.string()),
    data_nascimento: campoExtraidoSchema(z.string().describe('AAAA-MM-DD')),
    nome_mae: campoExtraidoSchema(z.string()),
    nome_pai: campoExtraidoSchema(z.string()),
    naturalidade: campoExtraidoSchema(z.string()),
    nacionalidade: campoExtraidoSchema(z.string()),
    data_emissao: campoExtraidoSchema(z.string()),
    observacoes: z.string().max(500).nullable(),
});
export const extracaoComprovanteResidenciaSchema = z.object({
    tipo: z.literal('comprovante_residencia'),
    nome_titular: campoExtraidoSchema(z.string()),
    endereco: campoExtraidoSchema(z.string()),
    cep: campoExtraidoSchema(z.string()),
    municipio: campoExtraidoSchema(z.string()),
    uf: campoExtraidoSchema(z.string()),
    data_referencia: campoExtraidoSchema(z.string().describe('AAAA-MM-DD')),
    emissor: campoExtraidoSchema(z.string()),
    observacoes: z.string().max(500).nullable(),
});
export const extracaoCertidaoCasamentoSchema = z.object({
    tipo: z.literal('certidao_casamento'),
    conjuge_1: campoExtraidoSchema(z.string()),
    conjuge_2: campoExtraidoSchema(z.string()),
    data_casamento: campoExtraidoSchema(z.string()),
    regime_bens: campoExtraidoSchema(regimeBensSchema),
    averbacoes: campoExtraidoSchema(z.string()),
    observacoes: z.string().max(500).nullable(),
});
export const extracaoIptuSchema = z.object({
    tipo: z.literal('iptu'),
    inscricao_imobiliaria: campoExtraidoSchema(z.string()),
    endereco: campoExtraidoSchema(z.string()),
    cep: campoExtraidoSchema(z.string()),
    municipio: campoExtraidoSchema(z.string()),
    proprietario: campoExtraidoSchema(z.string()),
    exercicio: campoExtraidoSchema(z.string()),
    observacoes: z.string().max(500).nullable(),
});
export const extracaoCpfSchema = z.object({
    tipo: z.literal('cpf'),
    nome: campoExtraidoSchema(z.string()),
    cpf: campoExtraidoSchema(z.string()),
    data_nascimento: campoExtraidoSchema(z.string()),
    observacoes: z.string().max(500).nullable(),
});
export const extracaoGenericaSchema = z.object({
    tipo: z.literal('outro'),
    descricao: z.string().max(500),
    campos: z.array(z.object({ nome: z.string(), valor: z.string(), confianca: z.number() })),
    observacoes: z.string().max(500).nullable(),
});
export const extracaoDocumentoSchema = z.discriminatedUnion('tipo', [
    extracaoIdentidadeSchema,
    extracaoComprovanteResidenciaSchema,
    extracaoCertidaoCasamentoSchema,
    extracaoIptuSchema,
    extracaoCpfSchema,
    extracaoGenericaSchema,
]);
/**
 * Esquema de extração de um tipo só. Enviado ao modelo no lugar da união completa: os provedores
 * de saída estruturada limitam o número de campos com tipos alternativos, e a união com os seis
 * tipos passa desse limite.
 */
export function schemaExtracaoPorTipo(tipo) {
    switch (tipo) {
        case 'identidade':
            return extracaoIdentidadeSchema;
        case 'comprovante_residencia':
            return extracaoComprovanteResidenciaSchema;
        case 'certidao_casamento':
            return extracaoCertidaoCasamentoSchema;
        case 'iptu':
            return extracaoIptuSchema;
        case 'cpf':
            return extracaoCpfSchema;
        default:
            return extracaoGenericaSchema;
    }
}
export const cnaePropostoSchema = z.object({
    codigo: z.string().describe('Formato 0000-0/00'),
    descricao: z.string(),
    principal: z.boolean(),
    justificativa: z.string().max(300),
});
/** Parecer da IA sobre a empresa: nome, CNAEs, objeto, natureza, porte e regime. */
export const propostaEmpresaSchema = z.object({
    nome_empresarial: z.object({
        principal: z.string(),
        alternativas: z.array(z.string()).max(2),
        observacoes: z.string().max(300),
    }),
    cnaes: z.array(cnaePropostoSchema).min(1).max(10),
    objeto_social: z.string().max(1500),
    natureza_juridica: z.object({
        codigo: z.string().describe('Ex.: 206-2 para LTDA, 213-5 para Empresário Individual'),
        descricao: z.string(),
    }),
    porte: porteSchema,
    regime_tributario: regimeTributarioSchema,
    raciocinio: z.string().max(600).describe('Três linhas explicando CNAEs, porte e regime.'),
    alertas: z.array(z.string().max(300)).max(10),
    confianca: z.number().min(0).max(1),
});
/** Perguntas que só o cliente pode responder, já redigidas para o WhatsApp. */
export const perguntasClienteSchema = z.object({
    perguntas: z.array(z.object({
        campo: z.string(),
        pergunta: z.string().max(400),
        motivo: z.string().max(300),
    })),
});
// ---------------------------------------------------------------------------
// Ficha de abertura: campo a campo, com origem e confiança
// ---------------------------------------------------------------------------
export const campoFichaSchema = z.object({
    chave: z.string(),
    grupo: z.enum(['empresa', 'endereco', 'socio', 'capital', 'atividade', 'tributario']),
    socio_id: z.uuid().nullable(),
    rotulo: z.string(),
    valor: z.unknown(),
    origem: origemCampoSchema,
    confianca: z.number().min(0).max(1),
    evidencia: z.string().nullable(),
    documento_id: z.uuid().nullable(),
    marcado: z.boolean(),
    motivo_marcacao: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// Portal do cliente: o que a página recebe
// ---------------------------------------------------------------------------
export const itemChecklistSchema = z.object({
    chave: z.string(),
    tipo: tipoDocumentoSchema,
    socio_id: z.uuid().nullable(),
    socio_nome: z.string().nullable(),
    titulo: z.string(),
    instrucao: z.string(),
    exemplos: z.string(),
    obrigatorio: z.boolean(),
    status: statusDocumentoSchema,
    motivo: z.string().nullable(),
    orientacao: z.string().nullable(),
    documento_ids: z.array(z.uuid()),
});
export const portalEstadoSchema = z.object({
    abertura: z.object({
        id: z.uuid(),
        estado: estadoSchema,
        tipo_empresa: tipoEmpresaSchema,
        etapa: etapaSchema,
        situacao: situacaoSchema,
        nome_contato: z.string(),
        nome_empresa: z.string().nullable(),
        criada_em: z.string(),
        /** portal = link direto; fluentforms = veio do site com contrato assinado e pagamento confirmado. */
        origem: z.enum(['portal', 'fluentforms']),
    }),
    consentimento: z.object({ aceito: z.boolean(), aceito_em: z.string().nullable() }),
    questionario: questionarioSchema.nullable(),
    checklist: z.array(itemChecklistSchema),
    documentos_completos: z.boolean(),
    /** A página só aceita envios quando a equipe pedir algo ao cliente; por padrão é só acompanhamento. */
    envio_habilitado: z.boolean(),
    pendencias: z.array(z.object({
        id: z.uuid(),
        tipo: z.enum(['documento', 'pergunta']),
        descricao: z.string(),
        criada_em: z.string(),
        resolvida_em: z.string().nullable(),
    })),
    linha_do_tempo: z.array(z.object({
        etapa: etapaSchema,
        numero: z.number(),
        titulo: z.string(),
        descricao: z.string(),
        status: z.enum(['concluida', 'atual', 'futura']),
        em: z.string().nullable(),
    })),
    proximo_passo_cliente: z.string().nullable(),
    documentos_finais: z.array(z.object({ nome: z.string(), url: z.string() })),
});
export const respostaPerguntaSchema = z.object({
    pendencia_id: z.uuid(),
    resposta: z.string().min(1).max(2000),
});
//# sourceMappingURL=schemas.js.map